import { InvitationData } from '@jobilla/nexus-sdk'
import { Table, TCell, TRow } from '@jobilla/starfield'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { SubtleLoader, TimeAgo } from '../../../../rapid-ui'

interface OrganizationInvitationsTableProps {
	invitations: InvitationData[] | undefined
	isLoading: boolean
	isPlaceholderData: boolean
	isSuccess: boolean
	onRevokeInvitation: (invitationId: string, email: string) => void
}

function StatusBadge({ status }: { status: InvitationData['status'] }) {
	const colorMap = {
		pending: 'bg-amber-100 text-amber-800',
		expired: 'bg-red-100 text-red-800',
		accepted: 'bg-green-100 text-green-800',
		revoked: 'bg-gray-100 text-gray-800',
	}

	return (
		<span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${colorMap[status]}`}>
			{status.charAt(0).toUpperCase() + status.slice(1)}
		</span>
	)
}

function RoleBadge({ role }: { role: InvitationData['role'] }) {
	return (
		<span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
			{role.charAt(0).toUpperCase() + role.slice(1)}
		</span>
	)
}

export function OrganizationInvitationsTable({
	invitations,
	isLoading,
	isPlaceholderData,
	isSuccess,
	onRevokeInvitation,
}: OrganizationInvitationsTableProps) {
	const { t } = useTranslation('common')

	if (isLoading) {
		return <SubtleLoader delay={0} />
	}

	if (isSuccess && (!invitations || invitations.length === 0)) {
		return (
			<div className="text-center py-12 text-gray-500">
				{t('organization.invitationsTable.noPending')}
			</div>
		)
	}

	return (
		<div
			className={`transition-opacity duration-200 ${isPlaceholderData ? 'opacity-50' : 'opacity-100'}`}
		>
			<Table
				head={
					<TRow>
						<TCell>{t('organization.invitationsTable.email')}</TCell>
						<TCell>{t('organization.invitationsTable.status')}</TCell>
						<TCell>{t('organization.invitationsTable.role')}</TCell>
						<TCell>{t('organization.invitationsTable.actions')}</TCell>
					</TRow>
				}
			>
				{isSuccess && invitations?.length
					&& invitations?.map(invitation => (
						<TRow key={invitation.invitationId}>
							<TCell>
								<div className="truncate max-w-xs" title={invitation.email}>
									{invitation.email}
								</div>
							</TCell>
							<TCell>
								<div className={'space-x-2'}>
									<StatusBadge status={invitation.status} />
									{invitation.expirationDate && (
										<TimeAgo
											date={new Date(invitation.expirationDate)}
										/>
									)}
								</div>
							</TCell>
							<TCell>
								<RoleBadge role={invitation.role} />
							</TCell>
							<TCell>
								<button
									onClick={() =>
										onRevokeInvitation(
											invitation.invitationId,
											invitation.email,
										)}
									className="px-3 py-1.5 text-sm font-medium text-red-700 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
								>
									{t('organization.invitationsTable.revoke')}
								</button>
							</TCell>
						</TRow>
					))}
			</Table>
		</div>
	)
}
